import { SxProps, Theme } from '@mui/system';
import {
  DataGridPremium,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridRowParams,
  GridSortModel,
  GridToolbarContainerProps,
} from '@mui/x-data-grid-premium';
import { useCallback, useContext, useMemo } from 'react';

import { DashboardContext } from '@/Contexts/DashboardContext';
import { UsersContext } from '@/Contexts/UsersContext';
import { ApplicationDataGridRowProps } from '@/models/Application';
import { UserType } from '@/models/User';
import { ApplicationStatus } from '@/utils/constants';
import { addEvenOddRowClassName, GridLocaleTextOverride } from '@/utils/dataGridUtil';

import ApplicationDataGridDetails from './ApplicationDataGridDetails';
import { LocaleContext } from '@/Contexts/LocaleContext';

interface PendingReviewApplicationDataGridProps {
  dataGridSx: SxProps<Theme>;
  loading: boolean;
  columns: GridColDef[];
  customExportToolbar: (props: GridToolbarContainerProps) => JSX.Element;
  onNoRowsOverlay?: (loading: boolean) => JSX.Element;
  onNoResultsOverlay?: () => JSX.Element;
  onFilterModelChange?: (f: GridFilterModel) => void;
  onSortModelChange?: (s: GridSortModel) => void;
  onPaginationModelChange?: (p: GridPaginationModel) => void;
}

export default function PendingReviewApplicationsDataGrid({
  dataGridSx,
  columns,
  loading,
  customExportToolbar,
  onNoRowsOverlay,
  onNoResultsOverlay,
  onFilterModelChange,
  onSortModelChange,
  onPaginationModelChange,
}: PendingReviewApplicationDataGridProps) {
  const { userCurrent } = useContext(UsersContext);
  const { locale } = useContext(LocaleContext);

  const {
    reviewApps,
    reviewApplicationSortModel,
    reviewApplicationFilterModel,
    reviewApplicationPaginationModel,
    columnVisibilityModel,
    updateReviewApps,
    updateReviewApplicationSortModel,
    updateReviewApplicationFilterModel,
    updateReviewApplicationPaginationModel,
  } = useContext(DashboardContext);

  const handleFilterModelChange = useCallback(
    (f: GridFilterModel) => {
      if (onFilterModelChange != null) {
        onFilterModelChange(f);
        return;
      }

      updateReviewApplicationFilterModel(f);
    },
    [onFilterModelChange, updateReviewApplicationFilterModel]
  );

  const handleSortModelChange = useCallback(
    (s: GridSortModel) => {
      if (onSortModelChange != null) {
        onSortModelChange(s);
        return;
      }

      updateReviewApplicationSortModel(s);
    },
    [onSortModelChange, updateReviewApplicationSortModel]
  );

  const handlePaginationModelChange = useCallback(
    (p: GridPaginationModel) => {
      if (onPaginationModelChange != null) {
        onPaginationModelChange(p);
        return;
      }

      updateReviewApplicationPaginationModel(p);
    },
    [onPaginationModelChange, updateReviewApplicationPaginationModel]
  );

  const handleRowUpdate = useCallback(
    (updatedRow: ApplicationDataGridRowProps) => {
      const updatedApplications = reviewApps.map((app) => {
        if (app.applicationId === updatedRow.applicationId) {
          return updatedRow;
        }
        return app;
      });

      updateReviewApps(updatedApplications);
    },
    [reviewApps, updateReviewApps]
  );

  const failedRows = useMemo(
    () =>
      reviewApps.filter(
        (app) =>
          app.failedApplicationTransactions != null && app.failedApplicationTransactions.length > 0
      ),
    [reviewApps]
  );

  const nonFailedRows = useMemo(
    () =>
      reviewApps.filter(
        (app) =>
          app.failedApplicationTransactions == null ||
          app.failedApplicationTransactions.length === 0
      ),
    [reviewApps]
  );

  const prioritizedRows = useMemo(
    () => [...failedRows, ...nonFailedRows],
    [failedRows, nonFailedRows]
  );

  return (
    <DataGridPremium<ApplicationDataGridRowProps>
      showToolbar
      localeText={GridLocaleTextOverride(locale)}
      getRowId={(row: ApplicationDataGridRowProps) => row.applicationId}
      getRowClassName={addEvenOddRowClassName((params) => {
        const currentRow = params.row as ApplicationDataGridRowProps;
        return currentRow.status !== ApplicationStatus.Closed &&
          currentRow.failedApplicationTransactions != null &&
          currentRow.failedApplicationTransactions.length > 0 &&
          userCurrent?.userType === UserType.Adf
          ? 'bc-error'
          : '';
      })}
      getDetailPanelContent={(params: GridRowParams<ApplicationDataGridRowProps>) => {
        const currentRow = params.row;
        if (currentRow.applicationId == null) return <></>;

        return currentRow != null ? (
          <ApplicationDataGridDetails applicationRow={params} updateRow={handleRowUpdate} />
        ) : (
          <> </>
        );
      }}
      getDetailPanelHeight={() => 'auto'}
      disableVirtualization
      initialState={{
        density: 'compact',
        sorting: {
          sortModel: [{ field: 'navResponse', sort: 'asc' }], // Default sort by "navResponse" in ascending order
        },
      }}
      disableDensitySelector
      disableColumnResize
      disableRowSelectionOnClick
      sx={dataGridSx}
      filterModel={
        reviewApplicationFilterModel != null ? reviewApplicationFilterModel : { items: [] }
      }
      pagination
      paginationModel={reviewApplicationPaginationModel}
      onPaginationModelChange={(p: GridPaginationModel) => handlePaginationModelChange(p)}
      onFilterModelChange={(f: GridFilterModel) => handleFilterModelChange(f)}
      sortModel={reviewApplicationSortModel != null ? reviewApplicationSortModel : []}
      onSortModelChange={(s: GridSortModel) => handleSortModelChange(s)}
      columnVisibilityModel={columnVisibilityModel}
      rows={prioritizedRows}
      columns={columns}
      slots={{
        noRowsOverlay: () => (onNoRowsOverlay != null ? onNoRowsOverlay(loading) : <> </>),
        noResultsOverlay: () => (onNoResultsOverlay != null ? onNoResultsOverlay() : <></>),
        toolbar: customExportToolbar,
      }}
    />
  );
}
